<template>
  <div
    class="c-content-carousel"
    :class="carouselContainerClass"
  >
    <div :class="isHeroCarousel ? '' : 'l-container'">
      <h2
        v-if="title"
        class="u-text-align-center c-content-carousel__title"
        :class="titleClass"
      >
        {{ title }}
      </h2>
      <div
        v-if="isHeroCarousel"
      >
        <flickity
          ref="flickity"
          class="flickity"
          :options="flickityHeroOptions"
          role="region"
          aria-label="Carousel"
          @change="slideEventListener"
        >
          <template v-for="(section, index) in sections">
            <hero
              :key="index"
              aria-live="assertive"
              v-bind="section"
            />
          </template>
        </flickity>
      </div>
      <div
        v-else-if="isRingOverlayCard"
        class="c-content-carousel__carousel-container"
      >
        <template v-if="flickityActive">
          <div :class="ringOverlayClass" />
          <flickity
            ref="flickity"
            class="flickity"
            :options="flickityRingOverlayOptions"
            role="region"
            aria-label="Carousel"
            @change="slideEventListener"
          >
            <template v-for="(section, index) in sections">
              <ring-overlay-card
                :key="index"
                v-bind="section"
              />
            </template>
          </flickity>
        </template>
        <div
          v-else
          class="
          l-container
          l-grid l-grid--4-col
        "
        >
          <template v-for="(section, index) in sections">
            <div
              :key="index"
              class="carousel-cell"
            >
              <ring-overlay-card
                :key="index"
                v-bind="section"
              />
            </div>
          </template>
        </div>
      </div>
      <div
        v-else-if="isNavCard"
        class="c-content-carousel__carousel-container"
      >
        <div :class="ringOverlayClass" />
        <flickity
          ref="flickity"
          class="flickity"
          :options="flickityRingOverlayOptions"
          role="region"
          aria-label="Carousel"
          @change="slideEventListener"
        >
          <template v-for="(section, index) in sections">
            <nav-card
              :key="index"
              v-bind="section"
              class="c-content-card-carousel__nav-item"
            />
          </template>
        </flickity>
      </div>
      <div
        v-else
        :class="carouselClass"
      >
        <div
          v-if="flickityActive"
          class="c-content-card-carousel__active-carousel"
        >
          <flickity
            ref="flickity"
            class="flickity"
            :options="flickityOptions"
          >
            <template v-for="(section, index) in sections">
              <div
                :key="index"
                class="carousel-cell"
              >
                <content-card
                  v-bind="section"
                  :not-clickable="true"
                />
              </div>
            </template>
          </flickity>
        </div>
        <div
          v-else
          class="
          c-content-card-carousel__disabled-carousel
          l-grid l-grid--3-col-fixed
        "
        >
          <template v-for="(section, index) in sections">
            <div
              :key="index"
              class="carousel-cell"
            >
              <content-card
                :key="index"
                v-bind="section"
              />
            </div>
          </template>
        </div>
      </div>
      <div
        v-if="cta && Object.keys(cta).length"
        class="c-content-carousel__cta-wrapper"
      >
        <btn
          v-bind="cta"
        >
          {{ cta.text }}
        </btn>
      </div>
    </div>
  </div>
</template>

<script>
import Btn from "@/components/button/Btn";
import flickityActive from "./flickityActive";
import ContentCard from "../card/ContentCard";
import Hero from "../hero/Hero.vue";
import RingOverlayCard from '../card/RingOverlayCard.vue';
import NavCard from '../nav/sharedComponents/NavCard.vue'

export default {
  name: "ContentCarousel",
  components: {
    Btn,
    ContentCard,
    Hero,
    RingOverlayCard,
    NavCard
  },
  mixins: [flickityActive],
  props: {
    entryTitle: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: ""
    },
    titleColor: {
      type: String,
      default: ""
    },
    titleTypography: {
      type: String,
      default: "t-heading-5"
    },
    titleMarginBottom: {
      type: String,
      default: ""
    },
    sections: {
      type: Array,
      default: () => [],
    },
    sideGradients: {
      type: Boolean,
      default: false
    },
    gradientHeight: {
      type: String,
      default: ''
    },
    buttonColor: {
      type: String,
      default: ''
    },
    cta: {
      type: Object,
      default: () => ({})
    },
    backgroundColor: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      carouselStart: true,
      carouselEnd: false,
      nextButtonDisabled: null,
      flickityOptions: {
        prevNextButtons: true,
        pageDots: true,
        wrapAround: true,
        freeScroll: true,
        cellAlign: "left",
        imagesLoaded: true,
        // any options from Flickity can be used
        // https://flickity.metafizzy.co/options.html
      },
      flickityRingOverlayOptions: {
        prevNextButtons: true,
        pageDots: true,
        wrapAround: true,
        freeScroll: true,
        cellAlign: "left",
        imagesLoaded: true,
      },
      flickityHeroOptions: {
        prevNextButtons: true,
        pageDots: true,
        wrapAround: true,
        cellAlign: "left",
        imagesLoaded: true,
        accessibility: true,
      },
    };
  },
  computed: {
    titleClass() {
      let results = []
      const titleColor = this.titleColor
      const titleTypography = this.titleTypography
      const titleMarginBottom = this.titleMarginBottom
      results.push(this.titleTypography);
      if (this.titleColor) {
        results.push(titleTypography + '--' + titleColor);
      }
      if (titleMarginBottom) {
        results.push(`u-margin-bottom-${titleMarginBottom}`)
      }
      return results;
    },
    carouselClass() {
      let results = ["c-content-card-carousel", 'u-padding-bottom-medium'];
      if (!this.carouselStart) {
        results.push("c-content-card-carousel--start-fade");
      }
      if (!this.carouselEnd) {
        results.push("c-content-card-carousel--end-fade");
      }
      return results;
    },
    carouselContainerClass() {
      let results = ['']
      const isHeroCarousel = this.isHeroCarousel
      if(isHeroCarousel) {
        results.push('c-content-carousel--hero')
      }
      if (this.buttonColor) {
        results.push("c-content-carousel--button-" + this.buttonColor)
      }
      if (this.backgroundColor != "base"); {
        results.push(" u-background-color-" + this.backgroundColor)
      }
      return results
    },
    ringOverlayClass() {
      let results = []
      const sideGradients = this.sideGradients
      if(sideGradients) {
        results.push('c-content-card-carousel--side-gradients')
      }
      if(this.gradientHeight) {
        results.push('c-content-card-carousel--side-gradients-' + this.gradientHeight)
      }
      return results
    },
    isHeroCarousel() {
      const sections = this.sections;
      let result = sections.every((item) => item.type == "Hero");
      return !!result;
    },
    isRingOverlayCard() {
      const sections = this.sections;
      let result = sections.every((item) => item.contentType == "ringOverlayCard");
      return !!result;
    },
    isNavCard() {
      const sections = this.sections;
      let result = sections.every((item) => item.contentType == "NavItem");
      return !!result;
    },
  },
  watch: {
    async products() {
      await this.$nextTick();
      this.flickityActivator();
    },
  },
  methods: {
    slideEventListener() {},
    flickityActivator() {
      if (this.sections.length > 4) {
        this.flickityActive = true;
        return;
      }
      if (window.innerWidth < 900) {
        if (this.sections.length > 2) {
          this.flickityActive = true;
          return;
        }
      }
      this.flickityActive = false;
      return;
    }
  },
};
</script>

<style lang="scss" scoped>
.c-content-carousel {
  position: relative;
  padding: $filter-section-padding;
  padding-left: rem(50px);
  padding-right: rem(50px);
  /* gives enough space for the dots under the carousel to render without
   * overlapping
   */
  padding-bottom: rem(35px);

  ::v-deep {
    .flickity-prev-next-button.previous {
      left: rem(-56px);
    }
    .flickity-prev-next-button.next {
      right: rem(-56px);
    }
  }
  &__carousel-container {
    position: relative;
    height: 100%;
  }
  &--hero {
    padding: 0 0 rem(70px);
    ::v-deep {
      .c-hero__flex {
        padding-left: 55px;
        padding-right: 55px;
      }
    }
    ::v-deep {
      .flickity-prev-next-button.previous {
        left: 10px;
      }
      .flickity-prev-next-button.next {
        right: 10px;
      }
    }
  }

  &__title {
    padding-top: rem(20px);
  }

  ::v-deep {
    .flickity .c-ring-overlay-card .c-btn {
      min-height: rem(55px);
      padding: rem(5px) rem(10px);
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}
.c-content-carousel--button-primary {
  ::v-deep {
    button.flickity-prev-next-button {
      @include themify($themes) {
        background-color: themed('background', 'dark');
      }
    }
    .flickity-prev-next-button svg.flickity-button-icon {
      @include themify($themes) {
        color: themed('foreground', 'white-text');
      }
    }
  }
}
.c-content-carousel--button-accent {
  ::v-deep {
    button.flickity-prev-next-button {
      @include themify($themes) {
        background-color: themed('background', 'accent');
      }
    }
    .flickity-prev-next-button svg.flickity-button-icon {
      @include themify($themes) {
        color: themed('foreground', 'white-text');
      }
    }
  }
}
.c-content-carousel__cta-wrapper {
  text-align: center;
  margin-top: rem(50px);
}
.c-content-card-carousel {
  position: relative;
  @include themify($themes) {
    border-color: themed("primary");
    background-color: themed("primary");
    color: themed("foreground", "white-text");
  }
  &__disabled-carousel {
    padding-top: rem(40px);
    padding-bottom: rem(40px);
    .carousel-cell {
      ::v-deep {
        .c-content-card__content {
          img {
            padding-bottom: rem(20px) !important;
          }
        }
      }
    }
  }

  &--side-gradients {
    top: 0;
    bottom: 0;
    pointer-events: none;
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 2;
    background: rgb(255,255,255);
    background: linear-gradient(90deg, 
      rgba(255,255,255,0.7) 0%, 
      rgba(255,255,255,0) 25%, 
      rgba(255,255,255,0) 75%,
      rgba(255,255,255,0.7) 100%);
  }

  &__nav-item {
    width: 24%;
    min-width: rem(300px);
    margin-right: rem(20px);

    height: rem(120px);
    @include respond(tab-land) {
      width: 50%;
    }
    @include respond(tab-port) {
      width: 50%;
    }
    @include respond(phone) {
      width: 100%;
    }
    display: flex;
    ::v-deep {
      .c-nav-card__wrapper {
        .c-nav-card__copy {
          align-self: center;
          .c-nav-card__header{
            margin-top: 0;
            padding-right: 0;
          }
        }
      }
    }
  }

  .flickity {
    height: 100%;
    &:focus-visible {
      outline: none !important;
    }
  }

  //flickity style overrides
  &__active-carousel {
    padding-top: rem(40px);
    padding-bottom: rem(40px);
    .carousel-cell {
      width: 100%;
      @include respond(tab-port) {
        width: 100%;
      }

      ::v-deep {
        .c-content-card__content {
          img {
            padding-bottom: rem(20px) !important;
          }
        }
      }
    }
  }

  &--end-fade {
    &:after {
      content: "";
      position: absolute;
      z-index: 1;
      top: 0;
      right: 0;
      pointer-events: none;
      width: rem(200px);
      height: 100%;
      @include respond(phone) {
        width: rem(150px);
      }
    }
  }

  &--start-fade {
    &:before {
      content: "";
      position: absolute;
      z-index: 1;
      top: 0;
      left: 0;
      pointer-events: none;
      width: rem(200px);
      height: 100%;
      @include respond(phone) {
        width: rem(150px);
      }
    }
  }

  .flickity-prev-next-button {
    z-index: 2;
    &:disabled {
      display: none;
    }
  }
  .flickity-prev-next-button.next {
    z-index: 3!important;
    right: rem(-35px);
    @media only screen and (max-width: 1700px) {
      right: rem(30px);
    }
  }
  .flickity-prev-next-button.previous {
    left: rem(-35px);
    @media only screen and (max-width: 1700px) {
      left: 10px;
    }
  }
}
</style>
